/**
 * PhotoSection Component
 * Section for adding photos to a check-in or note: multi-select from the
 * gallery, camera capture, web drag-and-drop and paste, and optional per-photo
 * alt text.
 */

import { AppImage } from '@/components/ui/app-image';
import { confirm } from '@/components/ui/confirmation-dialog';
import { Text } from '@/components/ui/text';
import { toast } from '@/components/ui/toast';
import { MAX_ALT_TEXT_LENGTH, MAX_PHOTOS_PER_CHECK_IN } from '@/constants/photos';
import { useAppTheme } from '@/providers/theme-provider';
import {
  describePickerError,
  ImagePickerResult,
  pickImagesFromGallery,
  prepareWebImageFiles,
  revokeBlobUris,
  showImagePickerOptions,
  takePhotoWithCamera,
} from '@/utils/image-picker';
import { getTransferFiles, validatePhotoBeforeUpload } from '@/utils/photo-validation';
import Ionicons from '@expo/vector-icons/Ionicons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  TextInput,
  View,
} from 'react-native';
import { PhotoAsset } from '../types';

export interface PhotoSectionProps {
  /** Array of photo assets */
  photos: PhotoAsset[];
  /** Callback when a photo is added (called once per photo, in order) */
  onAdd: (photo: PhotoAsset) => void;
  /** Callback when a photo is removed */
  onRemove: (photoId: string) => void;
  /** Update one photo (alt text). When omitted, no description control is shown. */
  onUpdatePhoto?: (photoId: string, patch: { altText: string }) => void;
  /** Maximum number of photos allowed */
  maxPhotos?: number;
  /** Whether the section is disabled */
  disabled?: boolean;
}

const THUMB_SIZE = 88;
const OVERLAY_HIT_SLOP = { top: 10, bottom: 10, left: 10, right: 10 };
/** Minimum touch target (Apple HIG / WCAG 2.5.5); hitSlop does nothing on web. */
const MIN_TOUCH_TARGET = 44;
const PICKER_QUALITY = 0.8;
const MAX_REACHED_TITLE = 'Maximum photos reached';

function maxReachedWarning(maxPhotos: number) {
  toast.warning(MAX_REACHED_TITLE, {
    description: `You can only upload up to ${maxPhotos} photo${maxPhotos === 1 ? '' : 's'}.`,
  });
}

/**
 * Validate picked images and turn the valid ones into local photo assets.
 * Invalid images are reported with a toast, skipped and their web preview
 * URLs released.
 */
async function toPhotoAssets(results: readonly ImagePickerResult[]): Promise<PhotoAsset[]> {
  const assets: PhotoAsset[] = [];
  for (const result of results) {
    if (result.cancelled || !result.uri) continue;
    const validation = await validatePhotoBeforeUpload({
      uri: result.uri,
      mimeType: result.type,
      size: result.size,
      fileName: result.name,
    });
    if (!validation.valid) {
      toast.error(validation.error || 'Invalid photo file');
      revokeBlobUris([result.uri]);
      continue;
    }
    assets.push({
      id: `photo-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
      uri: result.uri,
      mimeType: result.type,
      file: result.file,
      isUploading: false,
    });
  }
  return assets;
}

/**
 * Web only: accept image files dropped on, or pasted into, the element behind
 * `ref`. Text drops and pastes are left alone. The listeners attach to this
 * section only, so several sections on one screen do not all receive a drop.
 */
function useWebImageDropAndPaste(
  ref: React.RefObject<View | null>,
  enabled: boolean,
  onFiles: (files: File[]) => void
) {
  const [isDragActive, setIsDragActive] = useState(false);

  useEffect(() => {
    if (Platform.OS !== 'web' || !enabled) return;
    const node = ref.current as unknown as HTMLElement | null;
    if (!node || typeof node.addEventListener !== 'function') return;

    const handleDragOver = (event: DragEvent) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      event.preventDefault();
      setIsDragActive(true);
    };
    const handleDragLeave = () => setIsDragActive(false);
    const handleDrop = (event: DragEvent) => {
      const files = getTransferFiles(event.dataTransfer);
      setIsDragActive(false);
      if (files.length === 0) return;
      event.preventDefault();
      onFiles(files);
    };
    const handlePaste = (event: ClipboardEvent) => {
      const files = getTransferFiles(event.clipboardData);
      if (files.length === 0) return;
      event.preventDefault();
      onFiles(files);
    };

    node.addEventListener('dragenter', handleDragOver);
    node.addEventListener('dragover', handleDragOver);
    node.addEventListener('dragleave', handleDragLeave);
    node.addEventListener('drop', handleDrop);
    node.addEventListener('paste', handlePaste);
    return () => {
      node.removeEventListener('dragenter', handleDragOver);
      node.removeEventListener('dragover', handleDragOver);
      node.removeEventListener('dragleave', handleDragLeave);
      node.removeEventListener('drop', handleDrop);
      node.removeEventListener('paste', handlePaste);
    };
  }, [ref, enabled, onFiles]);

  return isDragActive;
}

interface PhotoThumbnailProps {
  photo: PhotoAsset;
  index: number;
  disabled: boolean;
  canEditAltText: boolean;
  onRemove: (photoId: string) => void;
  onEditAltText: (photoId: string) => void;
}

function PhotoThumbnail({
  photo,
  index,
  disabled,
  canEditAltText,
  onRemove,
  onEditAltText,
}: PhotoThumbnailProps) {
  const number = index + 1;
  const altText = photo.altText?.trim();
  const hasAltText = Boolean(altText);
  const showControls = !photo.isUploading && !disabled;

  return (
    <View
      className="relative rounded-lg overflow-hidden bg-surface-secondary"
      style={{ width: THUMB_SIZE, height: THUMB_SIZE }}
    >
      <AppImage
        source={{ uri: photo.uri }}
        cachePolicy="none"
        style={{ width: THUMB_SIZE, height: THUMB_SIZE }}
        accessibilityLabel={altText || `Check-in photo ${number}`}
      />

      {photo.isUploading && (
        <View
          className="absolute inset-0 items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          accessible
          accessibilityLabel={`Uploading photo ${number}`}
        >
          <ActivityIndicator size="small" color="#fff" />
        </View>
      )}

      {photo.uploadError && (
        <View
          className="absolute inset-0 items-center justify-center"
          style={{ backgroundColor: 'rgba(220, 38, 38, 0.9)' }}
          accessible
          accessibilityLabel={`Photo ${number} failed to upload: ${photo.uploadError}`}
        >
          <Ionicons name="alert-circle" size={24} color="#fff" />
        </View>
      )}

      {showControls && (
        <Pressable
          onPress={() => onRemove(photo.id)}
          className="absolute top-1 right-1 rounded-full items-center justify-center"
          style={{ width: 24, height: 24, backgroundColor: 'rgba(0,0,0,0.6)' }}
          hitSlop={OVERLAY_HIT_SLOP}
          accessibilityRole="button"
          accessibilityLabel={`Remove photo ${number}`}
        >
          <Ionicons name="close" size={14} color="#fff" />
        </Pressable>
      )}

      {showControls && canEditAltText && (
        // The pressable fills a 44pt corner; the visible pill stays small.
        <Pressable
          onPress={() => onEditAltText(photo.id)}
          className="absolute bottom-0 left-0 justify-end items-start p-1"
          style={{ width: MIN_TOUCH_TARGET, height: MIN_TOUCH_TARGET }}
          accessibilityRole="button"
          accessibilityLabel={`${hasAltText ? 'Edit' : 'Add'} description for photo ${number}`}
        >
          <View
            className="rounded px-1.5 py-0.5 flex-row items-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.6)', gap: 2 }}
          >
            {hasAltText && <Ionicons name="checkmark" size={10} color="#fff" />}
            <Text className="text-[10px] font-semibold" style={{ color: '#fff' }}>
              ALT
            </Text>
          </View>
        </Pressable>
      )}
    </View>
  );
}

interface AltTextEditorProps {
  photo: PhotoAsset;
  index: number;
  onChange: (photoId: string, patch: { altText: string }) => void;
  onDone: () => void;
}

function AltTextEditor({ photo, index, onChange, onDone }: AltTextEditorProps) {
  const value = photo.altText ?? '';
  return (
    <View className="mt-3">
      <View className="flex-row items-center justify-between mb-2">
        <Text className="text-sm font-medium text-muted-foreground">
          Describe photo {index + 1} (optional)
        </Text>
        <Pressable
          onPress={onDone}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Done editing description"
        >
          <Text className="text-sm font-medium text-primary">Done</Text>
        </Pressable>
      </View>
      <TextInput
        value={value}
        onChangeText={(altText) => onChange(photo.id, { altText })}
        placeholder="What's in the photo? Helps people using screen readers."
        placeholderTextColor="#9CA3AF"
        multiline
        maxLength={MAX_ALT_TEXT_LENGTH}
        className="text-sm text-foreground bg-surface border border-border rounded-lg p-3"
        style={{ minHeight: 64, textAlignVertical: 'top' }}
        accessibilityLabel={`Description for photo ${index + 1}`}
      />
      <Text className="text-xs text-muted-foreground mt-1 text-right">
        {value.length}/{MAX_ALT_TEXT_LENGTH}
      </Text>
    </View>
  );
}

export function PhotoSection({
  photos,
  onAdd,
  onRemove,
  onUpdatePhoto,
  maxPhotos = MAX_PHOTOS_PER_CHECK_IN,
  disabled = false,
}: PhotoSectionProps) {
  const theme = useAppTheme();
  const [isPickingImage, setIsPickingImage] = useState(false);
  const [editingAltTextId, setEditingAltTextId] = useState<string | null>(null);
  // Photos waiting to be handed to `onAdd`, one per render, so a parent that
  // appends from its render-time `photos` never loses a photo of a batch.
  const [pendingPhotos, setPendingPhotos] = useState<PhotoAsset[]>([]);
  const dropZoneRef = useRef<View>(null);
  // Synchronous in-flight guard shared by the button, drop and paste: state
  // updates land too late to stop a second flow (and a second crop modal).
  const isPickingRef = useRef(false);

  const remainingSlots = Math.max(0, maxPhotos - photos.length - pendingPhotos.length);
  const canAddMore = remainingSlots > 0;

  const photoCount = photos.length;
  useEffect(() => {
    if (pendingPhotos.length === 0) return;
    // Re-clamp against the current count: photos may have arrived while the
    // picker or crop modal was open.
    if (photoCount >= maxPhotos) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPendingPhotos([]);
      revokeBlobUris(pendingPhotos.map((photo) => photo.uri));
      return;
    }
    const [next, ...rest] = pendingPhotos;
    // Intentional: one onAdd per render, so the parent sees each previous add.
    setPendingPhotos(rest);
    onAdd(next);
  }, [pendingPhotos, onAdd, photoCount, maxPhotos]);

  const queuePickedImages = useCallback(
    async (results: readonly ImagePickerResult[]) => {
      const assets = await toPhotoAssets(results);
      const accepted = assets.slice(0, remainingSlots);
      revokeBlobUris(assets.slice(accepted.length).map((asset) => asset.uri));
      if (accepted.length > 0) {
        setPendingPhotos((prev) => [...prev, ...accepted]);
      }
    },
    [remainingSlots]
  );

  const runPicker = useCallback(
    async (pick: () => Promise<ImagePickerResult[]>, errorTitle: string) => {
      if (!canAddMore || disabled || isPickingRef.current) return;
      isPickingRef.current = true;
      setIsPickingImage(true);
      try {
        await queuePickedImages(await pick());
      } catch (error) {
        toast.error(errorTitle, { description: describePickerError(error) });
      } finally {
        isPickingRef.current = false;
        setIsPickingImage(false);
      }
    },
    [canAddMore, disabled, queuePickedImages]
  );

  const handleCameraPress = useCallback(
    () =>
      runPicker(
        async () => [
          await takePhotoWithCamera({
            allowsEditing: false,
            showAdvancedCrop: true,
            quality: PICKER_QUALITY,
          }),
        ],
        'Camera error'
      ),
    [runPicker]
  );

  const handleGalleryPress = useCallback(
    () =>
      runPicker(
        () =>
          pickImagesFromGallery({
            selectionLimit: remainingSlots,
            showAdvancedCrop: true,
            quality: PICKER_QUALITY,
          }),
        'Gallery error'
      ),
    [runPicker, remainingSlots]
  );

  const handleWebFiles = useCallback(
    (files: File[]) => {
      if (isPickingRef.current) return;
      if (!canAddMore) {
        maxReachedWarning(maxPhotos);
        return;
      }
      void runPicker(
        () => prepareWebImageFiles(files, { limit: remainingSlots, showAdvancedCrop: true }),
        'Could not add photo'
      );
    },
    [canAddMore, maxPhotos, remainingSlots, runPicker]
  );

  const isDragActive = useWebImageDropAndPaste(dropZoneRef, !disabled, handleWebFiles);

  const handleAddPress = useCallback(() => {
    if (!canAddMore) {
      maxReachedWarning(maxPhotos);
      return;
    }

    showImagePickerOptions(handleCameraPress, handleGalleryPress, () => setIsPickingImage(false));
  }, [canAddMore, maxPhotos, handleCameraPress, handleGalleryPress]);

  const handleRemove = useCallback(
    async (photoId: string) => {
      const confirmed = await confirm({
        title: 'Remove this photo?',
        description: 'Are you sure you want to remove this photo?',
        variant: 'destructive',
        confirmLabel: 'Remove',
      });
      if (confirmed) {
        const removed = photos.find((photo) => photo.id === photoId);
        setEditingAltTextId((current) => (current === photoId ? null : current));
        onRemove(photoId);
        if (removed) revokeBlobUris([removed.uri]);
      }
    },
    [onRemove, photos]
  );

  const closeAltTextEditor = useCallback(() => setEditingAltTextId(null), []);

  const canEditAltText = Boolean(onUpdatePhoto);
  const editingIndex = photos.findIndex((photo) => photo.id === editingAltTextId);
  const editingPhoto =
    canEditAltText && !disabled && editingIndex >= 0 ? photos[editingIndex] : null;

  return (
    <View
      ref={dropZoneRef}
      testID="photo-section-drop-zone"
      className="mb-4 rounded-lg"
      // Web: focusable so a click on the section followed by paste lands here.
      focusable={Platform.OS === 'web' && !disabled}
      style={
        isDragActive
          ? { outlineWidth: 2, outlineStyle: 'dashed', outlineColor: theme.colors.primary }
          : undefined
      }
    >
      <View className="flex-row items-center justify-between pt-3">
        <Text className="text-xl text-foreground flex-1">Photos</Text>
        <Text className="text-xs text-muted-foreground">
          {photos.length} of {maxPhotos}
        </Text>
      </View>

      <View className="mt-2">
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ paddingRight: 8 }}
        >
          <View className="flex-row gap-2">
            {photos.map((photo, index) => (
              <PhotoThumbnail
                key={photo.id}
                photo={photo}
                index={index}
                disabled={disabled}
                canEditAltText={canEditAltText}
                onRemove={handleRemove}
                onEditAltText={setEditingAltTextId}
              />
            ))}

            {canAddMore && !disabled && (
              <Pressable
                onPress={handleAddPress}
                disabled={isPickingImage}
                accessibilityRole="button"
                accessibilityLabel="Add photo"
                accessibilityHint={
                  Platform.OS === 'web' ? 'You can also drop or paste images here' : undefined
                }
                accessibilityState={{ disabled: isPickingImage, busy: isPickingImage }}
                className="rounded-lg items-center justify-center bg-muted border-2 border-dotted border-dim"
                style={{ width: THUMB_SIZE, height: THUMB_SIZE }}
              >
                {isPickingImage ? (
                  <ActivityIndicator size="small" color={theme.colors.primary} />
                ) : (
                  <Ionicons name="add" size={24} className="text-muted-foreground" />
                )}
              </Pressable>
            )}
          </View>
        </ScrollView>

        {editingPhoto && onUpdatePhoto && (
          <AltTextEditor
            photo={editingPhoto}
            index={editingIndex}
            onChange={onUpdatePhoto}
            onDone={closeAltTextEditor}
          />
        )}
      </View>
    </View>
  );
}

export default PhotoSection;
